import styled from 'styled-components'
import { getBoxShadow, keyframes } from '@/features/shared/styles/effects'
import { css } from '@/features/shared/styles'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { objectStorageUrl } from '@/features/shared/utils/objectStorageUrl'
import { routes } from '@shared/constants/routes'
import { Row } from '@shipt/design-system-layouts'
import { ButtonLink } from '@/features/shared/elements/Link/Buttons'
import { useScrollWithThreshold } from '@/features/shared/components/Headers/useScrollWithThreshold'
import { screenSizes } from '@shipt/design-system-themes'
import { trackElementClicked } from '@/features/shared/analytics/element'
import { getTargetCircleQueryParams } from '@/features/shared/components/Headers/Circle360Header/utils'
import { useSearchParams } from 'next/navigation'
import { TargetCircle360PromoBanner } from '@/features/entrypoint/components/TargetCircle360Page/TargetCircle360PromoBanner'
import { VDivider } from '@/features/shared/components/Dividers'
import { LogoIcon } from '@shipt/design-system-icons'
import { C360Logo } from '@/features/shared/components/C360Logo'

const Circle360Logo = () => (
  <Image
    alt="Target circle logo"
    width={18}
    height={18}
    src={objectStorageUrl('TargetCircle360JustCircleLogo.svg')}
  />
)

export const Circle360Header = () => {
  const isBelowThreshold = useScrollWithThreshold(71)
  const Header = isBelowThreshold ? AnimatedHeader : HeaderWrapper
  const searchParams = useSearchParams()
  const params = getTargetCircleQueryParams(searchParams)

  const handleClick = () => {
    trackElementClicked({
      type: 'button',
      content: isBelowThreshold ? 'Sign in on frozen header' : 'Sign in on header',
      display_sublocation: 'header',
    })
  }

  return (
    <>
      <Container>
        <Header>
          <GridLayoutContainer contentMaxWidth>
            <Row justify="space-between" align="center">
              <Row justify="start" align="center">
                <Logo aria-label="Shipt" />
                <LogoDivider />
                <C360Logo width={136} height={24} />
              </Row>
              <ButtonLink
                size={{ base: 'sm', md: 'md' }}
                href={`${routes.LOGIN.url}${params}`}
                aria-label="Sign in with Target Circle 360"
                onClick={handleClick}
                nativeLink
                trailingIcon={Circle360Logo}
              >
                Sign in with
              </ButtonLink>
            </Row>
          </GridLayoutContainer>
        </Header>
      </Container>
      <TargetCircle360PromoBanner />
    </>
  )
}

const Logo = styled(LogoIcon)`
  height: 26px;
`

const LogoDivider = styled(VDivider)`
  /* Shipt logo has built-in padding */
  margin-left: 0;
  height: 34px;
`

const headerHeight = css`
  height: 3.5rem;

  @media ${screenSizes.tablet} {
    height: 4.5rem;
  }
`

const Container = styled.div`
  ${headerHeight}
`

const HeaderWrapper = styled.header`
  background-color: ${({ theme }) => theme.white};
  ${headerHeight}
  display: flex;
  align-items: center;

  & > div {
    width: 100%;
  }
`

const AnimatedHeader = styled(HeaderWrapper)`
  top: 0;
  position: fixed;
  width: 100%;
  z-index: 999;

  ${getBoxShadow('rest', 'light')};

  --enter-y: -72px;
  --enter-opacity: 1;
  animation: ${keyframes.enter} 0.15s linear;
`
